<template>
  <div></div>
</template>

<script>
import { mapState } from 'vuex'
import { aclMixin } from 'src/plugins/mixins'
import { get } from 'lodash'

export default {
  name: 'intercom',

  mixins: [aclMixin],

  data () {
    return {
      intercomBannerHeight: 0,
      env: null,
      app_id: process.env.INTERCOM_APP_ID,
      timeInterval: null,
      alreadySetup: false
    }
  },

  computed: {
    ...mapState(['statics']),

    ...mapState('cache', ['currentCompany']),

    ...mapState('auth', ['profile', 'authenticated']),

    isWhiteLabel () {
      return get(this.statics, 'whitelabel', false)
    }
  },

  methods: {
    setup (newRoute = false) {
      if (!this.authenticated || !this.profile?.enabled || this.isWhiteLabel || this.alreadySetup) {
        if (this.alreadySetup) {
          this.prepareTopMenuFix(newRoute)
        }

        return
      }

      window.axios.get('/api/v1/profile/intercom-user-hash').then(response => {
        const payload = response.data || {}
        // env app_id wins for desktop builds (per-environment override)
        const appId = this.app_id || payload.app_id

        if (!payload.email || !payload.user_id) {
          console.warn('[Intercom] Skipping boot: incomplete payload', {
            has_user_id: !!payload.user_id,
            has_email: !!payload.email
          })
          return
        }

        console.log('[Intercom] Booting ...')
        if (window.Intercom) {
          window.Intercom('boot', { ...payload, app_id: appId })

          console.log('[Intercom] Boot results:', {
            has_user_id: !!payload.user_id,
            has_email: !!payload.email,
            has_name: !!payload.name,
            has_company: !!payload.company
          })

          this.alreadySetup = true
          this.prepareTopMenuFix(newRoute)
        } else {
          console.log('[Intercom] Not loaded')
        }
      }).catch(err => {
        console.log(err)
      })
    },

    prepareTopMenuFix (newRoute = false) {
      // Keep a single poller — without this a new interval was stacked on every
      // route change, so several loops ended up reflowing the layout at once.
      clearInterval(this.timeInterval)

      this.timeInterval = setInterval(() => {
        let intercomIframe = document.querySelector('[name=intercom-banner-frame]')
        let intercomIframeHeight = this.getIntercomIframeHeight(intercomIframe)

        if (intercomIframeHeight !== this.intercomBannerHeight || newRoute) {
          this.fixTopMenu(intercomIframeHeight)
        }

        this.intercomBannerHeight = intercomIframeHeight
      }, 1 * 1000)
    },

    fixTopMenu () {
      let intercomIframe = document.querySelector('[name=intercom-banner-frame]')
      let intercomIframeHeight = this.getIntercomIframeHeight(intercomIframe)
      let isTopNotification = intercomIframe ? intercomIframe.getBoundingClientRect().top === 0 : true

      let appElement = document.getElementById('q-app')
      let bodyElement = document.body

      if (!appElement) {
        return
      }

      const hasTopBanner = intercomIframeHeight > 0 &&
        intercomIframe &&
        intercomIframe.offsetWidth === window.innerWidth &&
        isTopNotification

      if (hasTopBanner) {
        // Intercom paints the banner as a fixed, full-width overlay pinned to the
        // top of the viewport. Reserve exactly its height in a single place by
        // pushing the whole app shell down and shrinking it, so every descendant
        // (header, sidebar, message composer and its line picker) re-flows inside
        // the remaining height instead of overflowing below the fold. Doing it on
        // the root avoids the stacked-offset bug that clipped the bottom of the
        // composer when an in-app banner was visible alongside the Intercom one.
        bodyElement.style.marginTop = '0'
        appElement.style.marginTop = `${intercomIframeHeight}px`
        appElement.style.height = `calc(100% - ${intercomIframeHeight}px)`
        // expose the banner height so top-anchored UI (e.g. the incoming-call toast) can offset itself
        document.documentElement.style.setProperty('--intercom-banner-height', intercomIframeHeight + 'px')
      } else {
        bodyElement.style.marginTop = ''
        appElement.style.marginTop = ''
        appElement.style.height = ''
        document.documentElement.style.removeProperty('--intercom-banner-height')
      }
    },

    getIntercomIframeHeight (intercomIframe) {
      let intercomIframeInnerDoc = intercomIframe ? (intercomIframe.contentDocument || intercomIframe.contentWindow.document) : null
      let intercomIframeDomBody = intercomIframeInnerDoc ? intercomIframeInnerDoc.getElementById('intercom-container-body') : null
      return intercomIframeDomBody ? intercomIframeDomBody.clientHeight : 0
    },

    processSetup (newRoute = false) {
      if (!this.hasReporterAccess &&
        !this.isWhiteLabel &&
        this.profile &&
        process.env.INTERCOM_APP_ID !== '') {
        this.setup(newRoute)
      }
    }
  },

  created () {
    this.$router.beforeEach((to, from, next) => {
      this.processSetup(true)
      next()
    })

    this.processSetup()

    this.$VueEvent.listen('headerNotificationClosed', () => {
      this.fixTopMenu()
    })
  },

  watch: {
    'statics.whitelabel': function (newValue) {
      if (!newValue) {
        this.processSetup()
      }
    }
  },

  beforeDestroy () {
    clearInterval(this.timeInterval)

    const appElement = document.getElementById('q-app')
    if (appElement) {
      appElement.style.marginTop = ''
      appElement.style.height = ''
    }
    document.body.style.marginTop = ''
    document.documentElement.style.removeProperty('--intercom-banner-height')
  }
}
</script>
